import React, { useMemo } from 'react';

import { compareBoardColumns } from '@aline/common';
import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { useCellStyles } from 'src/components/reports';
import { ProgressiveColumnIcon } from 'src/components/workflow/board/settings/ColumnItem';
import { getColumnPositionInCategory } from 'src/components/workflow/phase-column-helpers';
import {
  SigningLeaveDialog,
  UnparkConfirmDialog,
  useWorkflowColumnChangeGuard,
} from 'src/components/workflow/use-workflow-column-change-guard';
import { WorkflowListGroupLabelCell } from 'src/components/workflow/workflow-list/cells/WorkflowListGroupLabelCell';
import type { WorkflowListGridRow } from 'src/components/workflow/workflow-list/WorkflowsList';
import {
  getIsWorkflowListGroupRow,
  getIsWorkflowListWorkflowRow,
  getWorkflowIdFromListRow,
} from 'src/components/workflow/workflow-list/WorkflowsList';
import { ColumnPickerPopover } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';

export function WorkflowListColumnCell({
  field,
  row,
}: GridCellParams<WorkflowListGridRow>): JSX.Element | null {
  const cellClasses = useCellStyles();
  const { getPickerChipClassName, pickerChipIconClassName, pickerChipLabelClassName } =
    usePickerChipStyles();
  const { data: boardsData } = Apollo.useGetBoardsQuery();
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const workflowRow = getIsWorkflowListWorkflowRow(row) ? row : null;
  const currentBoard = boardsData?.boards?.find(board =>
    board.columns?.some(column => column.id === workflowRow?.column.id)
  );
  const sortedColumns = useMemo(
    () => [...(currentBoard?.columns ?? [])].sort(compareBoardColumns),
    [currentBoard?.columns]
  );

  const currentColumnPosition = useMemo(() => {
    if (!workflowRow?.column || !sortedColumns.length) return { index: 1, total: 1 };
    return getColumnPositionInCategory(workflowRow.column, sortedColumns);
  }, [workflowRow?.column, sortedColumns]);

  const {
    confirmUnparkMove,
    dismissSigningLeave,
    dismissUnpark,
    handleColumnChange,
    pendingUnparkColumn,
    signingLeaveOpen,
  } = useWorkflowColumnChangeGuard({
    columns: sortedColumns,
    currentColumn: workflowRow?.column,
    onConfirmColumnChange: async (columnId, { confirmUnpark }) => {
      if (!workflowRow) return;
      await updateWorkflow({
        variables: {
          input: {
            confirmUnpark,
            props: { columnId },
            workflowId: getWorkflowIdFromListRow(workflowRow),
          },
        },
      });
    },
    workflowState: workflowRow?.state,
  });

  if (getIsWorkflowListGroupRow(row)) {
    return (
      <WorkflowListGroupLabelCell count={row.workflowCount} field={field} label={row.groupLabel} />
    );
  }
  if (!workflowRow) return null;

  return (
    <div className={cellClasses.cell} onClick={event => event.stopPropagation()}>
      <ColumnPickerPopover
        columns={sortedColumns}
        renderTrigger={(_, isOpen) => (
          <div className={getPickerChipClassName(isOpen)}>
            <ProgressiveColumnIcon
              boxSize={PICKER_CHIP_ICON_SIZE}
              category={workflowRow.column.category}
              className={pickerChipIconClassName}
              color={workflowRow.column.color}
              iconSize={PICKER_CHIP_ICON_SIZE}
              index={currentColumnPosition.index}
              showBackground={false}
              showBorder={false}
              total={currentColumnPosition.total}
            />
            <Typography noWrap className={pickerChipLabelClassName} variant="body2">
              {workflowRow.column.name}
            </Typography>
          </div>
        )}
        selectedColumnId={workflowRow.column.id}
        onColumnChange={handleColumnChange}
      />
      <SigningLeaveDialog open={signingLeaveOpen} onClose={dismissSigningLeave} />
      <UnparkConfirmDialog
        currentColumnName={workflowRow.column.name}
        open={Boolean(pendingUnparkColumn)}
        targetColumnName={pendingUnparkColumn?.name}
        onClose={dismissUnpark}
        onConfirm={confirmUnparkMove}
      />
    </div>
  );
}
